import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { generatePath, useNavigate } from "react-router-dom";

import { Button, Grid, Stack } from "@mui/material";
import { ArrowBack, CloseOutlined, ContentCopyOutlined, CreateOutlined } from "@mui/icons-material";

import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";

import VehicleContract, { ApplicationClipboard } from "./VehicleContract";
import vehicleContractService from "../../../services/vehicleContractService";
import { VehicleContractPreviewDto } from "../../../interfaces/VehicleContract";
import { PublicRoutesConfig, CommonRoutesConfig } from "../../RoutesConfig";
import useUserContext from "../../../hooks/useUserContext";
import usePrint from "../../../common/hooks/usePrint";
import PrintFooter from "../../../common/components/PrintFooter";
import { LoadingButton } from "@mui/lab";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { useConfirm } from "material-ui-confirm";
import Success from "../../shared/SuccessModal";
import { dateToStringShort } from "../../../common/utils/dateTime";

export default function Preview() {
  const { id: paramsId } = useParams();
  const vehicleContractId: number = parseInt(paramsId as string);

  const loading = useAxiosLoader();
  const confirm = useConfirm();

  const navigate = useNavigate();
  const { isOwner } = useUserContext();
  const [vehicleContract, setVehicleContract] = useState<VehicleContractPreviewDto>();
  const { printComponentRef, print } = usePrint();

  const [isAnnulled, setIsAnnulled] = useState(false);

  useEffect(() => {
    if (isNaN(vehicleContractId)) {
      navigate(CommonRoutesConfig.NotFound.Path, { replace: true });
    }

    (async () => {
      await vehicleContractService
        .getVehicleContractAsync(vehicleContractId)
        .then(({ data: vehicleContract }) => setVehicleContract(vehicleContract));
    })();
  }, []);

  const handleEdit = () => {
    navigate(generatePath(PublicRoutesConfig.Vehicle.Edit.Path, { id: vehicleContractId.toString() }));
  };

  const annulle = async () => {
    await vehicleContractService.annulleVehicleContractAsync(vehicleContractId).then(({ data: error }) => {
      if (error) toast.error(error);
      else setIsAnnulled(true);
    });
  };

  const renderAnnulledSuccess = () => (
    <Success title="Успешно анулиране" routePath={PublicRoutesConfig.Vehicle}>
      {`на договор за прехвърляне на МПС ${vehicleContract?.registrationNumber} / ${dateToStringShort(
        vehicleContract?.issueDate
      )}`}
    </Success>
  );

  const renderPrintTemplate = () => (
    <div style={{ display: "none" }}>
      <Stack spacing={3} ref={printComponentRef}>
        <VehicleContract model={vehicleContract} />
        <PrintFooter />
      </Stack>
    </div>
  );

  const renderPreview = () => (
    <Stack spacing={3}>
      <Grid container wrap="nowrap" justifyContent="space-between" spacing={1}>
        <Grid item>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
            Назад
          </Button>
        </Grid>
        <Grid container item justifyContent="end" spacing={1}>
          {isOwner(vehicleContract?.ownerUserIds) && !vehicleContract?.isRejected && !vehicleContract?.isAnnulled && (
            <>
              <Grid item>
                <LoadingButton
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={<CloseOutlined />}
                  onClick={() =>
                    confirm({ title: "Действието ще анулира изпълнението на услугата в СППТЕАУ. Сигурни ли сте?" })
                      .then(async () => await annulle())
                      .catch(() => {})
                  }
                >
                  Анулирай
                </LoadingButton>
              </Grid>
              <Grid item>
                <Button startIcon={<CreateOutlined />} onClick={() => handleEdit()}>
                  Редактирай
                </Button>
              </Grid>
            </>
          )}
          <Grid item>
            <CopyToClipboard
              text={ApplicationClipboard.getText(vehicleContract?.application)}
              onCopy={() => {
                toast.success("Данните се копираха успешно!");
              }}
            >
              <Button startIcon={<ContentCopyOutlined />}>Копирай</Button>
            </CopyToClipboard>
          </Grid>
          <Grid item>
            <Button startIcon={PublicRoutesConfig.Vehicle.Print.Icon} onClick={print}>
              {PublicRoutesConfig.Vehicle.Print.Label}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <VehicleContract model={vehicleContract} />
    </Stack>
  );

  return (
    <>
      {isAnnulled ? (
        renderAnnulledSuccess()
      ) : (
        <>
          {renderPreview()}
          {renderPrintTemplate()}
        </>
      )}
    </>
  );
}
